import { useCallback, useEffect, useState } from 'react';
import { FiInfo } from 'react-icons/fi';
import { ClbCheckbox } from '@celebration/react/ClbCheckbox/ClbCheckbox';
import { useTranslation } from 'react-i18next';
import Shape from '../../../../../../common/components/shape';
import Input from '../../../../../../common/components/input';
import SelectAutocomplete from '../../../../../../common/components/select-autocomplete';
import './user-form-infos.scss';
import IUserFormInfosProps from './interfaces/IUserFormInfosProps';
import UserFormService from '../../services/user-form.service';
import UserFormValidationService from '../../services/user-form.validation.service';
import UserType from './enums/user-type';
import Row from '../../../../../../common/components/row';
import { ICheckboxProps } from '../../../../../branches/components/branch-form/components/branch-form-regions/interfaces/ICheckboxProps';
import { useToast } from '../../../../../../app/contexts/toast/toast.context';
import { ReactComponent as SpanIcon } from '../../../../../../common/assets/svg/spam.svg';
import CarriersService from '../../../../../carriers/services/carriers.service';
import ICarriersResponse from '../../../../../carriers/interfaces/ICarriersResponse';
import Auth from '../../../../../../app/contexts/auth';

function UserFormInfos({
    handleUserFormChange,
    userFormData,
    inputErrors,
}: Readonly<IUserFormInfosProps>) {
    const { t } = useTranslation();

    const userDataTitle = t('NovoUsuario');
    const basicDataSubtitle = t('DadosUsuarios');
    const allowedSystemsSubtitle = t('SistemasPermitidos');

    const [carriers, setCarriers] = useState([] as ICarriersResponse[]);

    const { setToast } = useToast();
    const { isThirdUser } = Auth.useAuth();

    const selectedCheck = useCallback(
        (props: ICheckboxProps) => {
            const systemsNotUpdated = [...userFormData.systems];

            systemsNotUpdated.forEach(system => {
                if (system.id === props.id) {
                    system.isChecked = props.checked;
                }
            });

            handleUserFormChange(userFormData);
        },
        [handleUserFormChange, userFormData],
    );

    const handleCarrierSearch = async (value: string) => {
        const response = await CarriersService.getCarriersList(1, 20, {
            carrierName: value,
        }).catch(err => {
            setToast('negative', t('Erro'), err.message);
        });

        return response?.data ?? [];
    };

    const onCarriersResult = (data: ICarriersResponse[]) =>
        setCarriers([...data]);

    useEffect(() => {
        if (!isThirdUser()) {
            CarriersService.getCarriersList(1, 20, { carrierName: '' })
                .then(response => {
                    const { data } = response;
                    setCarriers([...data]);
                })
                .catch(err => {
                    setToast('negative', t('Erro'), err.message);
                });
        }
    }, [setToast, t, isThirdUser]);

    const renderFormHeader = () => {
        return (
            !isThirdUser() && (
                <>
                    <h1>{userDataTitle}</h1>

                    <Row className="ambev-user-warning">
                        <span>
                            <FiInfo className="info-icon" />
                            {t('InfoAdicionarUsuario')}
                            <b>&nbsp;{t('SolicitacoesMinusculo')}</b>
                        </span>
                    </Row>

                    <hr />
                </>
            )
        );
    };

    const renderUserData = () => {
        return (
            <>
                <h2>{basicDataSubtitle}</h2>

                <div className="user-data-div">
                    <Input
                        id="user-name"
                        label={t('Nome')}
                        value={userFormData.name}
                        className="user-name"
                        placeholder={t('InsiraNomeUsuario')}
                        onChange={(e: any) =>
                            handleUserFormChange({
                                property: 'name',
                                newValue: e.target.value,
                            })
                        }
                        error={!!inputErrors.name}
                        helperText={UserFormValidationService.getError(
                            inputErrors.name,
                            t,
                        )}
                        dataTestId="user-name"
                    />

                    <Input
                        id="user-email"
                        label={t('Email')}
                        value={userFormData.email}
                        className="user-email"
                        placeholder={t('InsiraEmailUsuario')}
                        onChange={(e: any) =>
                            handleUserFormChange({
                                property: 'email',
                                newValue: e.target.value,
                            })
                        }
                        error={!!inputErrors.email}
                        helperText={UserFormValidationService.getError(
                            inputErrors.email,
                            t,
                        )}
                        dataTestId="user-email"
                    />

                    <SelectAutocomplete
                        label={t('TipoUsuario')}
                        placeholder={t('TipoUsuario')}
                        defaultValue={Number(userFormData.type)}
                        value={Number(userFormData.type)}
                        options={Object.keys(UserType)
                            .filter(x => !Number.isNaN(Number(x)))
                            .map(x => ({
                                label: UserFormService.TranslateUserTypeLabel(
                                    UserType[Number(x)],
                                ),
                                value: Number(x),
                            }))}
                        onChange={(e: any) =>
                            handleUserFormChange({
                                property: 'type',
                                newValue: e,
                            })
                        }
                        dataTestId="user-type"
                    />

                    {!isThirdUser() && (
                        <SelectAutocomplete
                            label={t('Transportadora')}
                            placeholder={t('Transportadora')}
                            options={carriers.map(carrier => ({
                                label: carrier.tradeName,
                                value: carrier.id,
                            }))}
                            dataTestId="user-carrier"
                            className="user-carrier"
                            value={userFormData.userCarrier.name}
                            onChange={(e: any) => {
                                const carrier = carriers.find(
                                    carrier => carrier.id === e,
                                );

                                handleUserFormChange({
                                    property: 'userCarrier',
                                    newValue: {
                                        carrierId: e,
                                        name: carrier?.tradeName,
                                        sulaHubId: carrier?.sulaHubId,
                                    },
                                });
                            }}
                            onSearch={handleCarrierSearch}
                            onFetchDataResults={onCarriersResult}
                            error={!!inputErrors.userCarrier?.carrierId}
                            helperText={UserFormValidationService.getError(
                                inputErrors.userCarrier?.carrierId,
                                t,
                            )}
                        />
                    )}
                </div>
            </>
        );
    };

    const renderUserAllowedSystems = () => {
        return (
            <>
                <h2>{allowedSystemsSubtitle}</h2>

                <div className="user-allowed-systems">
                    {userFormData.systems.map((item, key) => (
                        <ClbCheckbox
                            key={key}
                            name="Checkbox"
                            checked={item.isChecked}
                            id={item.id}
                            data-testid={`checkbox-${item.id}`}
                            onChange={(e: any) => {
                                selectedCheck({
                                    id: e.target.id,
                                    checked: e.target.checked,
                                });
                            }}
                        >
                            {item.label}
                        </ClbCheckbox>
                    ))}
                </div>
                {inputErrors.systems && (
                    <span className="systems-checked-error">
                        <SpanIcon />
                        {UserFormValidationService.getError(
                            inputErrors.systems,
                            t,
                        )}
                    </span>
                )}
            </>
        );
    };

    return (
        <Shape>
            {renderFormHeader()}

            {renderUserData()}

            {renderUserAllowedSystems()}
        </Shape>
    );
}

export default UserFormInfos;
